/**
 * Pure, directly testable: the two bits of arithmetic/parsing behind the
 * sidebar's manual-resize handle and its Wissen-mode `knowledgeSid` lookup
 * (operator request 2026-08-17). Split out of `ha-app.component.ts` for the
 * same reason `nav-item-filters.ts` is — that component pulls in the app's
 * full DI/component graph (Router, routes.ts, lottie-interactivity) at
 * import time, which drags in browser-only globals that don't exist in
 * vitest's `node` test environment without an escalating chain of shims.
 * This file has none of that, so it can be unit tested by importing it
 * directly (review 01, MAJOR, Linse C: ha-app.component.ts had no test file
 * at all before this).
 */

/**
 * The width the sidebar should be dragged to, given how far the pointer has
 * moved since the drag started. Unclamped on purpose — the sidebar's own
 * CSS `min-width`/`max-width` (todo_sidebar.css) are what the BROWSER actually
 * renders it at; this stays the single source of truth for the raw request,
 * not a second place that would have to duplicate those bounds.
 */
export function computeDraggedWidth(startWidth: number, startX: number, currentX: number): number {
    return startWidth + (currentX - startX);
}

/**
 * The ROUTED KnowledgeObject's uid, parsed straight from the already-computed
 * `currentPath` (the `/knowledge/:sid/:slug?` pattern from routes.ts) —
 * `<knowledge-sidebar-list>` isn't itself the route's rendered element
 * (`knowledge-overview-view` is), so it can't receive `sid` as a route param
 * the way that view does.
 *
 * `undefined` both when `currentMode` isn't `'wissen'` and on the bare
 * `/knowledge` list (no second path segment) — the caller doesn't need to
 * distinguish the two, both mean "no row should be marked `.selected`".
 *
 * `currentMode`'s type mirrors `appState.currentMode` (`app.state.ts`)
 * literally rather than importing it — a value import there would drag in
 * the same heavy DI/component graph this file exists to stay decoupled from
 * (see module docblock); a plain type import is erased at compile time and
 * would be safe too, but duplicating the three-way union is simpler than a
 * `typeof appState.currentMode` indirection for something this small.
 */
export function parseKnowledgeSid(currentMode: 'todo' | 'wissen' | 'resource' | 'linkview' | 'personal-organizer', currentPath: string): string | undefined {
    if (currentMode !== 'wissen') {
        return undefined;
    }
    return currentPath.split('/')[1];
}

/**
 * The ROUTED Linkview's uid, parsed the same way `parseKnowledgeSid` parses
 * a KnowledgeObject's — `<linkview-sidebar-list>` isn't the `/linkview/:sid`
 * route's rendered element either (`<o2d-linkview-view>` is), so it needs
 * the same sidecar lookup (Anke, 2026-09-04: "mehrere Linkviews... in der
 * Sidebar als Liste").
 *
 * `currentMode`'s type duplicates the four-way union rather than importing
 * it, for the same reason `parseKnowledgeSid` does (see its own doc comment
 * just above).
 */
export function parseLinkviewSid(currentMode: 'todo' | 'wissen' | 'resource' | 'linkview' | 'personal-organizer', currentPath: string): string | undefined {
    if (currentMode !== 'linkview') {
        return undefined;
    }
    return currentPath.split('/')[1];
}

/**
 * The `style` attribute value for the `<sidebar>` element: restores a
 * previously dragged width (review 01, MAJOR/INFO override 2026-08-17:
 * "sollte auch gemerkt werden") as an explicit inline style ONLY when the
 * user has actually dragged the handle before (`sidebarWidth !== null`);
 * otherwise `''` so the CSS default width (todo_sidebar.css) still
 * applies untouched.
 */
export function computeSidebarStyle(sidebarWidth: number | null): string {
    return sidebarWidth !== null ? `width: ${sidebarWidth}px` : '';
}

/**
 * Double-click-toggle target width (operator request 2026-08-28): flips
 * between the minimum width and a second, "everything readable" width
 * (`--sidebar-min-width` / `--sidebar-readable-width`, theme-sidebar-default.css /
 * theme-default.css). Picks
 * whichever of the two the current width is FARTHER from — a midpoint
 * comparison rather than an exact-equality check against one of the two
 * bounds, so it still does the right thing from any in-between width (e.g.
 * a manually dragged width, or the very first double-click before either
 * bound has ever been applied) instead of only toggling cleanly once the
 * sidebar is already exactly at one of the two values.
 */
export function computeToggledSidebarWidth(currentWidth: number, minWidth: number, readableWidth: number): number {
    return currentWidth < (minWidth + readableWidth) / 2 ? readableWidth : minWidth;
}
